import { Action, Dispatch } from "redux";
import { AxiosError, AxiosResponse } from "axios";
import { notification } from "antd";
import { Key } from "react";

import { addEditRequestType } from "../../../models";
import {
  GET_OFFER_REQUESTS,
  GET_OFFER_REQUEST_BY_ID,
  GET_OFFER_REQUEST_SENT,
  IS_LOADING_SAVE,
  IS_LOADING_SEND,
} from "./types";
import { ApiService, ApiServiceFormData } from "../../../services";
import {
  NavigationPath,
  addEditRequestFormData,
  downloadBlobFile,
  history,
  mapDataRows,
  showErrorNotification,
} from "../../../utils";

const setLoadingSave = (loading: boolean) => ({
  type: IS_LOADING_SAVE,
  payload: loading,
});

const setLoadingSend = (loading: boolean) => ({
  type: IS_LOADING_SEND,
  payload: loading,
});

export const addRequest =
  (files: File[], request: addEditRequestType) =>
  async (dispatch: Dispatch<Action>) => {
    dispatch(setLoadingSave(true));

    const formData = addEditRequestFormData(files, request);

    ApiServiceFormData.post("/offer-request", formData)
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 201) {
          showErrorNotification("Kreiranje zahtjeva za ponudu nije uspjelo");
          return;
        }
        notification.success({
          message: "Zahtjev za ponudu je kreiran.",
        });
        history.navigate(NavigationPath.OFFER_REQUESTS_ROUTE);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Kreiranje zahtjeva za ponudu nije uspjelo", err);
      })
      .finally(() => {
        dispatch(setLoadingSave(false));
      });
  };

export const getRequestById =
  (id: string, clearCurrent = true) =>
  async (dispatch: Dispatch<Action>) => {
    if (clearCurrent) {
      dispatch({
        type: GET_OFFER_REQUEST_BY_ID,
        payload: null,
      });
    }
    ApiService.get(`offer-request/${id}`)
      .then((res: AxiosResponse) => {
        const { status, data } = res;

        if (status !== 200) {
          showErrorNotification("Učitavanje zahtjeva za ponudu nije uspjelo");
          return;
        }

        dispatch({
          type: GET_OFFER_REQUEST_BY_ID,
          payload: data,
        });
      })
      .catch((err: AxiosError) => {
        showErrorNotification(
          "Učitavanje zahtjeva za ponudu nije uspjelo",
          err
        );
      });
  };

export const editRequest =
  (
    files: File[],
    removedFileIds: string[],
    request: addEditRequestType,
    id: string
  ) =>
  async (dispatch: Dispatch<Action>) => {
    dispatch(setLoadingSave(true));

    const formData = addEditRequestFormData(files, request, removedFileIds);

    ApiServiceFormData.put(`offer-request/${id}`, formData)
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 200) {
          showErrorNotification("Izmjena zahtjeva za ponudu nije uspjela");
          return;
        }
        notification.success({
          message: "Izmjene su sačuvane.",
        });
        // Reload so the attachment list shows the saved files with their ids.
        getRequestById(id, false)(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Izmjena zahtjeva za ponudu nije uspjela", err);
      })
      .finally(() => dispatch(setLoadingSave(false)));
  };

// Reloads only the "sent to" list so "VEĆ POSLANO" shows the companies just
// sent to, without resetting unsaved edits in the request form.
const refreshOfferRequestSent =
  (id: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`offer-request/${id}`)
      .then((res: AxiosResponse) => {
        dispatch({
          type: GET_OFFER_REQUEST_SENT,
          payload: res.data.offerRequestSent ?? [],
        });
      })
      .catch(() => {
        // Sending already succeeded; the list refreshes on the next page load.
      });
  };

export const sendRequest =
  (offerRequestId: string, insuranceIds: string[]) =>
  async (dispatch: Dispatch<Action>) => {
    dispatch(setLoadingSend(true));
    ApiService.post(`offer-request/send-email`, {
      offerRequestId,
      insuranceIds,
    })
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 200) {
          showErrorNotification(
            "Slanje zahtjeva osiguravajućim društvima nije uspjelo"
          );
          return;
        }
        notification.success({
          message: "Zahtjev je poslan osiguravajućim društvima.",
        });
        refreshOfferRequestSent(offerRequestId)(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification(
          "Slanje zahtjeva osiguravajućim društvima nije uspjelo",
          err
        );
      })
      .finally(() => dispatch(setLoadingSend(false)));
  };

export const downloadFile =
  (id: string, fileId: string, filename: string) =>
  async (dispatch: Dispatch<Action>) => {
    ApiService.get(`offer-request/${id}/file-download/${fileId}`, {
      responseType: "blob",
    })
      .then((res) => {
        downloadBlobFile(new Blob([res.data]), filename);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Preuzimanje fajla nije uspjelo", err);
      });
  };

export const getOfferRequests = () => async (dispatch: Dispatch<Action>) => {
  ApiService.get("/offer-request")
    .then((res: AxiosResponse) => {
      const { status, data } = res;
      if (status !== 200) {
        showErrorNotification("Učitavanje zahtjeva za ponudu nije uspjelo");
        return;
      }

      const offerRequests = mapDataRows(data.rows);

      dispatch({
        type: GET_OFFER_REQUESTS,
        payload: offerRequests,
      });
    })
    .catch((err: AxiosError) => {
      showErrorNotification("Učitavanje zahtjeva za ponudu nije uspjelo", err);
    });
};

export const deleteOfferRequest =
  (ids: Key[]) => async (dispatch: Dispatch<Action>) => {
    ApiService.delete(`/offer-request/`, {
      data: { ids },
    })
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 204) {
          showErrorNotification("Brisanje zahtjeva za ponudu nije uspjelo");
          return;
        }
        notification.success({
          message:
            ids.length === 1
              ? "Zahtjev za ponudu je obrisan."
              : "Zahtjevi za ponudu su obrisani.",
        });
        getOfferRequests()(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Brisanje zahtjeva za ponudu nije uspjelo", err);
      });
  };
